import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchRecipeCatalog, fetchRecipeCatalogSuggest } from '../utils/recipeCatalogApi';
import { TIER_ORDER } from '../components/RecipeBook/recipeBookConstants';

const DEFAULT_FILTERS = {
	search: '',
	health_tier: '',
	ingredient: '',
	sort: 'health',
	page: 1
};

const DEBOUNCE_MS = 300;

export default function useRecipeCatalog({ catalogEndpoint, suggestEndpoint, csrfToken, enabled = true }) {
	const [filters, setFilters] = useState(DEFAULT_FILTERS);
	const [meta, setMeta] = useState(null);
	const [recipes, setRecipes] = useState([]);
	const [pagination, setPagination] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [suggestions, setSuggestions] = useState({ titles: [], ingredients: [] });
	const [suggestOpen, setSuggestOpen] = useState(false);
	const abortRef = useRef(null);
	const suggestAbortRef = useRef(null);

	const loadCatalog = useCallback(async () => {
		if (!enabled || !catalogEndpoint) return;

		if (abortRef.current) abortRef.current.abort();
		abortRef.current = new AbortController();

		setLoading(true);
		setError(null);

		const browsingChapters =
			!filters.search?.trim() && !filters.health_tier && !filters.ingredient?.trim();

		try {
			const data = await fetchRecipeCatalog({
				endpoint: catalogEndpoint,
				csrfToken,
				params: {
					search: filters.search,
					health_tier: filters.health_tier,
					ingredient: filters.ingredient,
					sort: filters.sort,
					page: filters.page,
					per_page: browsingChapters ? 48 : 24
				}
			});

			setMeta(data.meta || null);
			setRecipes(data.recipes || []);
			setPagination(data.pagination || null);
		} catch (err) {
			if (err.name === 'AbortError') return;
			setError(err.message || 'Failed to load recipe book');
			setMeta(null);
			setRecipes([]);
			setPagination(null);
		} finally {
			setLoading(false);
		}
	}, [enabled, catalogEndpoint, csrfToken, filters]);

	useEffect(() => {
		if (!enabled) return undefined;

		const timer = setTimeout(() => {
			loadCatalog();
		}, DEBOUNCE_MS);

		return () => clearTimeout(timer);
	}, [loadCatalog, enabled]);

	useEffect(() => {
		if (!enabled || !suggestEndpoint) return undefined;

		const query = (filters.search || filters.ingredient || '').trim();
		if (query.length < 2) {
			setSuggestions({ titles: [], ingredients: [] });
			return undefined;
		}

		if (suggestAbortRef.current) suggestAbortRef.current.abort();

		const timer = setTimeout(async () => {
			try {
				const data = await fetchRecipeCatalogSuggest({
					endpoint: suggestEndpoint,
					csrfToken,
					query
				});
				setSuggestions({
					titles: data.titles || [],
					ingredients: data.ingredients || []
				});
			} catch {
				setSuggestions({ titles: [], ingredients: [] });
			}
		}, 200);

		return () => clearTimeout(timer);
	}, [enabled, suggestEndpoint, csrfToken, filters.search, filters.ingredient]);

	const updateFilter = (key, value) => {
		setFilters((prev) => ({
			...prev,
			[key]: value,
			page: key === 'page' ? value : 1
		}));
	};

	const clearFilters = () => {
		setFilters({ ...DEFAULT_FILTERS });
		setSuggestOpen(false);
		setSuggestions({ titles: [], ingredients: [] });
	};

	const recipesByTier = useMemo(() => {
		const grouped = {};
		TIER_ORDER.forEach((tier) => {
			grouped[tier] = [];
		});
		recipes.forEach((recipe) => {
			const tier = recipe.health_tier || 'health_first';
			if (!grouped[tier]) grouped[tier] = [];
			grouped[tier].push(recipe);
		});
		return grouped;
	}, [recipes]);

	const hasActiveFilters =
		Boolean(filters.search?.trim()) ||
		Boolean(filters.health_tier) ||
		Boolean(filters.ingredient?.trim());

	const reload = () => loadCatalog();

	return {
		meta,
		recipes,
		recipesByTier,
		pagination,
		loading,
		error,
		filters,
		updateFilter,
		clearFilters,
		hasActiveFilters,
		suggestions,
		suggestOpen,
		setSuggestOpen,
		reload
	};
}
